import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import { LogIn, LogOut } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const BACKEND = "http://3.109.108.187/api";

axios.defaults.withCredentials = true; // ⭐ MUST for JWT cookies

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);

  // ------------------------
  // CHECK LOGIN STATUS
  // ------------------------
  const checkAuth = async () => {
    try {
      const res = await axios.get(`${BACKEND}/admin/me`);
      if (res.status === 200) {
        setIsAuthenticated(true);
      }
    } catch (err) {
      setIsAuthenticated(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // ------------------------
  // LOGIN HANDLER
  // ------------------------
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${BACKEND}/admin/login`, loginData);

      toast.success("Login successful!");
      setIsAuthenticated(true);

    } catch (error) {
      toast.error("Invalid credentials. Default: admin / amrut2025");
    } finally {
      setLoading(false);
    }
  };

  // ------------------------
  // LOGOUT HANDLER
  // ------------------------
  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND}/admin/logout`);
    } catch (error) {}
    toast.success("Logged out");
    setIsAuthenticated(false);
  };

  // ------------------------
  // LOGIN PAGE
  // ------------------------
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-green-50">
        <Navbar />
        <div className="min-h-[calc(100vh-80px)] flex items-center justify-center pt-20 px-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-green-600 flex items-center justify-center mx-auto mb-4">
                <LogIn className="text-white" size={32} />
              </div>
              <h2 className="text-3xl font-bold text-gray-800">Admin Login</h2>
              <p className="text-gray-600 mt-2">Access the admin panel</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Username</label>
                <input
                  type="text"
                  value={loginData.username}
                  onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                  placeholder="admin"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
                <input
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                  placeholder="••••••••"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary disabled:opacity-50"
              >
                {loading ? 'Logging in...' : 'Login'}
              </button>

              <p className="text-center text-sm text-gray-500">
                Default credentials: <span className="font-semibold">admin / amrut2025</span>
              </p>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ------------------------
  // ADMIN PANEL UI
  // ------------------------
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 to-green-600 mt-20">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Admin Panel</h1>
              <p className="text-white/90">Manage Amrut Mahotsav content</p>
            </div>
            <button
              onClick={handleLogout}
              className="px-6 py-3 bg-white text-orange-600 rounded-full font-semibold hover:shadow-lg transition-all flex items-center space-x-2"
            >
              <LogOut size={18} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Admin Tabs */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <Tabs defaultValue="live-updates" className="space-y-8">
          <TabsList className="grid w-full grid-cols-6 gap-2">
            <TabsTrigger value="live-updates">Live Updates</TabsTrigger>
            <TabsTrigger value="events">Events</TabsTrigger>
            <TabsTrigger value="gallery">Gallery</TabsTrigger>
            <TabsTrigger value="ceo-message">CEO Message</TabsTrigger>
            <TabsTrigger value="spotlight">Spotlight</TabsTrigger>
            <TabsTrigger value="faqs">FAQs</TabsTrigger>
          </TabsList>

          <AdminLiveUpdates />
          <AdminEvents />
          <AdminGallery />
          <AdminCEOMessage />
          <AdminSpotlight />
          <AdminFAQs />
        </Tabs>
      </div>

      <Footer />
    </div>
  );
};

//
// ─── LIVE UPDATES ─────────────────────────────────────────────
//
const AdminLiveUpdates = () => {
  const [updates, setUpdates] = useState([]);
  const [formData, setFormData] = useState({ title: '', description: '', event_date: '' });

  useEffect(() => {
    fetchUpdates();
  }, []);

  const fetchUpdates = async () => {
    try {
      const res = await axios.get(`${BACKEND}/live-updates`);
      setUpdates(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  try {
    await axios.post(`${BACKEND}/live-updates`, formData, {
      withCredentials: true   // ⭐ VERY IMPORTANT
    });
    toast.success("Live update added");
    setFormData({ title: '', description: '', event_date: '' });
    fetchUpdates();
  } catch (error) {
    toast.error("Failed to add update");
  }
};


  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND}/live-updates/${id}`);
      toast.success("Deleted");
      fetchUpdates();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  return (
    <TabsContent value="live-updates" className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-2xl font-bold mb-4">Add Live Update</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input className="w-full px-4 py-3 border rounded-lg"
            type="text"
            placeholder="Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
          />
          <textarea className="w-full px-4 py-3 border rounded-lg"
            placeholder="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
          />
          <input className="w-full px-4 py-3 border rounded-lg"
            type="date"
            value={formData.event_date}
            onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
            required
          />
          <button className="btn-primary" type="submit">Add Update</button>
        </form>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-2xl font-bold mb-4">Existing Updates</h3>

        {updates.map((u) => (
          <div key={u.id} className="flex justify-between items-center p-4 border rounded-lg">
            <div>
              <h4 className="font-bold">{u.title}</h4>
              <p className="text-gray-600">{u.description}</p>
            </div>

            <button
              onClick={() => handleDelete(u.id)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg"
            >
              Delete
            </button>
          </div>
        ))}
      </div>
    </TabsContent>
  );
};

//
// OTHER TABS (PLACEHOLDER)
//
const AdminEvents = () => (
  <TabsContent value="events">
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h3 className="text-2xl font-bold">Events</h3>
    </div>
  </TabsContent>
);

const AdminGallery = () => {
  const [images, setImages] = useState([]);
  const [preview, setPreview] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    file: null,
  });

  useEffect(() => {
    fetchGallery();
  }, []);

  // Fetch images
  const fetchGallery = async () => {
    try {
      const res = await axios.get(`${BACKEND}/gallery`);
      setImages(res.data);
    } catch (error) {
      console.log(error);
    }
  };

  // File Change Handler
  const handleFile = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, file });
    setPreview(URL.createObjectURL(file));
  };

  // Submit Form
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.file) {
      toast.error("Please select an image");
      return;
    }

    const data = new FormData();
    data.append("title", formData.title);
    data.append("description", formData.description);
    data.append("file", formData.file);

    try {
      await axios.post(`${BACKEND}/gallery`, data, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true
      });

      toast.success("Image uploaded!");
      setFormData({ title: "", description: "", file: null });
      setPreview(null);
      fetchGallery();

    } catch (error) {
      toast.error("Upload failed");
    }
  };

  // Delete Image
  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND}/gallery/${id}`, { withCredentials: true });
      toast.success("Deleted");
      fetchGallery();
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  return (
    <TabsContent value="gallery" className="space-y-6">
      {/* Upload Form */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-2xl font-bold mb-4">Upload Image</h3>

        <form onSubmit={handleSubmit} className="space-y-4">

          <input
            type="text"
            className="w-full px-4 py-3 border rounded-lg"
            placeholder="Title"
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            required
          />

          <textarea
            className="w-full px-4 py-3 border rounded-lg"
            placeholder="Description (optional)"
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
          />

          <input
            type="file"
            accept="image/*"
            onChange={handleFile}
            className="w-full"
            required
          />

          {preview && (
            <img
              src={preview}
              className="h-40 object-cover rounded-lg shadow-md"
              alt="Preview"
            />
          )}

          <button className="btn-primary" type="submit">
            Upload
          </button>
        </form>
      </div>

      {/* Existing Images */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-2xl font-bold mb-4">Gallery Images</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {images.map((img) => (
            <div key={img.id} className="border rounded-lg p-4 shadow">
              <img
                src={`http://3.109.108.187/assets/${img.image_url}`}
                alt={img.title}
                className="w-full h-40 object-cover rounded"
              />
              <h4 className="font-bold mt-2">{img.title}</h4>
              <p className="text-gray-600 text-sm">{img.description}</p>

              <button
                onClick={() => handleDelete(img.id)}
                className="mt-3 w-full bg-red-600 text-white py-2 rounded"
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      </div>
    </TabsContent>
  );
};


const AdminCEOMessage = () => (
  <TabsContent value="ceo-message">
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h3 className="text-2xl font-bold">CEO Message</h3>
    </div>
  </TabsContent>
);

const AdminSpotlight = () => {
  const [spots, setSpots] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    week: "",
    is_active: true
  });

  const [imageFile, setImageFile] = useState(null);

  const fetchSpotlight = async () => {
    try {
      const res = await axios.get(`${BACKEND}/spotlight`);
      setSpots(res.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchSpotlight();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const form = new FormData();
    form.append("title", formData.title);
    form.append("description", formData.description);
    form.append("week", formData.week);
    form.append("is_active", formData.is_active);
    form.append("image", imageFile);

    try {
      await axios.post(`${BACKEND}/spotlight`, form, {
        withCredentials: true,
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      toast.success("Spotlight added!");

      setFormData({
        title: "",
        description: "",
        week: "",
        is_active: true
      });
      setImageFile(null);

      fetchSpotlight();
    } catch (err) {
      toast.error("Failed to add spotlight");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND}/spotlight/${id}`, {
        withCredentials: true
      });

      toast.success("Deleted!");
      fetchSpotlight();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  return (
    <TabsContent value="spotlight" className="space-y-6">

      {/* ADD SPOTLIGHT */}
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <h3 className="text-2xl font-bold mb-4">Add Spotlight</h3>

        <form onSubmit={handleSubmit} className="space-y-4">

          <input
            className="w-full px-4 py-3 border rounded-lg"
            placeholder="Title"
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            required
          />

          <textarea
            className="w-full px-4 py-3 border rounded-lg"
            placeholder="Description"
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            required
          />

          <input
            className="w-full px-4 py-3 border rounded-lg"
            placeholder="Week (e.g., Week 1)"
            value={formData.week}
            onChange={(e) =>
              setFormData({ ...formData, week: e.target.value })
            }
            required
          />

          <input
            type="file"
            accept="image/*"
            onChange={(e) => setImageFile(e.target.files[0])}
            className="w-full"
            required
          />

          <button className="btn-primary px-6 py-3 text-white rounded-lg bg-blue-600">
            Add Spotlight
          </button>
        </form>
      </div>

      {/* LIST SPOTLIGHTS */}
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <h3 className="text-2xl font-bold mb-4">Existing Spotlight</h3>

        {spots.map((spot) => (
          <div
            key={spot.id}
            className="p-4 border rounded-lg mb-3 flex justify-between"
          >
            <div>
              <h4 className="font-bold">{spot.title}</h4>
              <p>{spot.description}</p>

              <img
                src={spot.image_url}
                alt={spot.title}
                className="w-32 h-20 object-cover mt-2 rounded"
              />

              <p className="text-gray-500 text-sm">Week: {spot.week}</p>
            </div>

            <button
              onClick={() => handleDelete(spot.id)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg"
            >
              Delete
            </button>
          </div>
        ))}
      </div>

    </TabsContent>
  );
};


const AdminFAQs = () => {
  const [faqs, setFaqs] = useState([]);
  const [formData, setFormData] = useState({
    question: "",
    answer: "",
    order: 0,
  });

  // Fetch FAQs
  const fetchFaqs = async () => {
    try {
      const res = await axios.get(`${BACKEND}/faqs`);
      setFaqs(res.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, []);

  // Submit FAQ
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await axios.post(`${BACKEND}/faqs`, formData, {
        withCredentials: true
      });

      toast.success("FAQ added successfully!");

      setFormData({ question: "", answer: "", order: 0 });
      fetchFaqs();

    } catch (error) {
      toast.error("Failed to add FAQ");
    }
  };

  // Delete FAQ
  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND}/faqs/${id}`, {
        withCredentials: true
      });

      toast.success("FAQ deleted");
      fetchFaqs();

    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  return (
    <TabsContent value="faqs" className="space-y-6">
      {/* Add FAQ */}
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <h3 className="text-2xl font-bold mb-4">Add FAQ</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            className="w-full px-4 py-3 border rounded-lg"
            placeholder="Question"
            value={formData.question}
            onChange={(e) => setFormData({ ...formData, question: e.target.value })}
            required
          />

          <textarea
            className="w-full px-4 py-3 border rounded-lg"
            placeholder="Answer"
            value={formData.answer}
            onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
            required
          />

          <input
            type="number"
            className="w-full px-4 py-3 border rounded-lg"
            placeholder="Order"
            value={formData.order}
            onChange={(e) => setFormData({ ...formData, order: e.target.value })}
          />

          <button className="btn-primary">Add FAQ</button>
        </form>
      </div>

      {/* List FAQs */}
      <div className="bg-white p-6 rounded-xl shadow-lg">
        <h3 className="text-2xl font-bold mb-4">Existing FAQs</h3>

        {faqs.map((f) => (
          <div key={f.id} className="p-4 border rounded-lg mb-3 flex justify-between items-start">
            <div>
              <h4 className="font-bold">{f.question}</h4>
              <p className="text-gray-700">{f.answer}</p>
              <p className="text-sm text-gray-500">Order: {f.order_no}</p>
            </div>

            <button
              onClick={() => handleDelete(f.id)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg"
            >
              Delete
            </button>
          </div>
        ))}
      </div>
    </TabsContent>
  );
};

export default AdminPanel;
